import {
  Outlet,
  useNavigate,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import { Spinner } from "@livingmap/hippo";
import { useTranslation } from "react-i18next";

import {
  useGetMapsQuery,
  useGetMMSStatusQuery,
  usePublishEditsForMapByIdMutation,
} from "@redux/services/mms";
import { Language, Project } from "@redux/services/types";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
  setMapID,
  setDefaultLanguage,
  setFeatureID,
} from "@redux/slices/applicationSlice";
import { getProjectNames } from "@utils";
import styles from "./FeaturesView.module.scss";
import PageTitle from "@components/PageTitle/PageTitle";
import NavigationTopBar from "@components/NavigationTopBar/NavigationTopBar";
import { useKeycloak } from "@react-keycloak/web";
import { KeycloakTokenParsed } from "keycloak-js";
import ContentPanel from "@components/ContentPanel/ContentPanel";
import { FC, useState } from "react";
import SideSheet from "@components/SideSheet/SideSheet";
import EditFeatureSidebar from "@components/EditFeatureSidebar/EditFeatureSidebar";
import useSideSheet from "./hooks/useSideSheet";
import LivingMap from "@livingmap/core-mapping";
import { GridApi } from "ag-grid-community";

export interface ParsedToken extends KeycloakTokenParsed {
  upn: string;
}

type ContextType = {
  setMapInstance?: (map: LivingMap) => void;
  setGridApi?: (api: GridApi) => void;
};

interface Props {
  onSignOut: () => void;
}

const FeaturesView: FC<Props> = ({ onSignOut }) => {
  const { t, i18n } = useTranslation();
  const { keycloak, initialized } = useKeycloak();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { mapID, featureID, defaultLanguage } = useAppSelector(
    (state) => state.application,
  );

  const { data, isLoading } = useGetMapsQuery();
  const { data: mmsStatusData } = useGetMMSStatusQuery(mapID, { skip: !mapID });
  const [publishEditsForMap] = usePublishEditsForMapByIdMutation();
  const [mapInstance, setMapInstance] = useState<LivingMap | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const {
    categoryData,
    deleteFeature,
    featureData,
    handleDataChanged,
    iconData,
    isFetchingSingleFeature,
    isLoading: isLoadingSideSheet,
    revertFeatureChanges,
    unarchiveFeature,
    updateFeature,
    handleSidebarClose,
  } = useSideSheet({
    mapID,
    featureID,
    mapInstance,
    gridApi,
  });

  const handleProjectSelect = (project: Project) => {
    dispatch(setMapID(project.id));
    dispatch(setDefaultLanguage(project.defaultLanguage));
    dispatch(setFeatureID(null));
  };

  const projectNames = getProjectNames(data);

  const activeProjectName = projectNames?.find(
    (project) => project.id === mapID,
  );
  const activeProject = data?.data.find((project) => project.id === mapID);

  if (!initialized) return null;

  const token = keycloak.tokenParsed as ParsedToken;
  const pathName = location.pathname;

  const sideSheetContent = (
    <EditFeatureSidebar
      isLoading={isLoadingSideSheet}
      icons={iconData?.data}
      allCategories={categoryData?.data}
      onSidebarCloseClick={handleSidebarClose}
      onEditFeatureSave={(data) =>
        updateFeature({ mapID, featureID, featureData: data })
      }
      onFeatureDelete={() => deleteFeature({ mapID, featureID })}
      onUnarchiveFeature={() => unarchiveFeature({ mapID, featureID })}
      // onFeatureSidebarReady={handleFeatureSidebarReady}
      onRevertAllChanges={() => revertFeatureChanges({ mapID, featureID })}
      onDataChanged={handleDataChanged}
      {...(isFetchingSingleFeature ? {} : featureData)}
    />
  );
  const featureName = isFetchingSingleFeature
    ? ""
    : featureData &&
      featureData.label.name?.find(
        (name: { lang: Language | undefined }) =>
          name.lang === activeProject?.languages.default.code,
      )?.text;

  return isLoading ||
    !mapID ||
    !mmsStatusData ||
    !activeProject ||
    !activeProjectName ||
    !projectNames ? (
    <div className={styles.loaderContainer} data-qa="features-view-loading">
      <Spinner dataQA="loading-spinner" size={36} />
    </div>
  ) : (
    <div className={styles.container} data-qa="features-view">
      <NavigationTopBar
        dataQA="features-view-navigation-top-bar"
        activeLanguage={defaultLanguage}
        languages={activeProject?.languages.available}
        activeProject={activeProjectName}
        projects={projectNames}
        onProjectChange={handleProjectSelect}
        changes={{
          features: mmsStatusData.unpublished_features,
          deletedFeatures: mmsStatusData.deleted_features,
        }}
        onPublishChanges={() => publishEditsForMap(mapID)}
        userProfile={token}
        onLanguageChange={(lang) => {
          dispatch(setDefaultLanguage(lang.code));
          i18n.changeLanguage(lang.code);
        }}
        userMenuItems={[
          {
            name: t("header.menu_items.sign_out"),
            icon: "logout",
            onClick: onSignOut,
            type: "primary",
          },
        ]}
        tabItems={[
          { text: t("header.menu_items.map_view"), url: "/features/map-view" },
          {
            text: t("header.menu_items.list_view"),
            url: "/features/list-view",
          },
        ]}
        initialPath={pathName}
        onTabChange={(tab) => navigate(tab.url)}
      />
      <div className={styles.contentContainer}>
        <ContentPanel dataQA="features-view-content-panel">
          <PageTitle
            dataQA="features-view-page-title"
            heading={t("views.features.title")}
            subheading={t("views.features.description")}
          />

          <div className={styles.outletContainer}>
            <Outlet
              context={{ setMapInstance, setGridApi } satisfies ContextType}
            />
          </div>
        </ContentPanel>
        {featureID && (
          <SideSheet
            title={featureName}
            dataQA="features-view-side-sheet"
            footer={null}
            children={sideSheetContent}
            onClose={handleSidebarClose}
          />
        )}
      </div>
    </div>
  );
};

export function useSetSideSheetInstances() {
  return useOutletContext<ContextType>();
}

export default FeaturesView;
